import JSZip from 'jszip';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { handleErrorToast, Modal } from 'src/components/molecules';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';
import { PaginatedTable } from 'src/components/organisms';
import {
  FileUploadResult,
  UploadSource,
  useValidateBulkCaseUploadAndGenerateUrlMutation,
} from 'src/graphql';
import { EvidenceItem } from 'src/hooks/cases/useUploadCases';
import allowedFileTypes from 'src/shared/allowed-file-types.json';
import { FeatureFlag, State } from 'src/types';
import {
  BATCH_SIZE,
  batchFiles,
  UPLOAD_CONCURRENCY,
  uploadWithConcurrency,
} from 'src/utils/uploadBatch';
import { uploadToPresignedUrl } from 'src/utils/uploadToPresignedUrl';
import {
  ButtonRow,
  UploadEvidenceContainer,
  UploadMessage,
} from './UploadEcidence.styles';

interface BulkFileUploadItem {
  filename: string;
  size: number;
  mimeType: string;
  filepath: string;
  fileExtension: string;
  file?: File;
}

const zipMimeTypeForExtension = (extension: string): string =>
  (allowedFileTypes.mimeByExtension as Record<string, string>)[
    extension.toLowerCase()
  ] ?? '';

const MAX_ZIP_ARCHIVE_SIZE_IN_BYTES = 500 * 1024 * 1024;
const MAX_EXTRACTED_FILE_SIZE_IN_BYTES = 100 * 1024 * 1024;
const MAX_ZIP_UNCOMPRESSED_TOTAL_SIZE_IN_BYTES = 500 * 1024 * 1024;

const FILESYSTEM_METADATA_BASENAMES = new Set([
  '.ds_store',
  'thumbs.db',
  'desktop.ini',
]);

const isFilesystemMetadataEntry = (path: string): boolean => {
  const segments = path.split('/').filter(Boolean);
  const basename = (segments[segments.length - 1] ?? '').toLowerCase();
  return (
    segments.includes('__MACOSX') ||
    FILESYSTEM_METADATA_BASENAMES.has(basename) ||
    basename.startsWith('._')
  );
};

const getDeclaredUncompressedSize = (
  entry: JSZip.JSZipObject,
): number | undefined => {
  const data = (entry as unknown as { _data?: { uncompressedSize?: number } })
    ._data;
  return typeof data?.uncompressedSize === 'number'
    ? data.uncompressedSize
    : undefined;
};

export const UploadEvidence = ({
  importedCaseIds,
  uploadEvidences,
  setUploadEvidences,
  onUploadingChange,
}: {
  importedCaseIds: string[];
  setUploadEvidences: React.Dispatch<React.SetStateAction<EvidenceItem[]>>;
  uploadEvidences: EvidenceItem[];
  onUploadingChange?: (isUploading: boolean) => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isParsingZip, setIsParsingZip] = useState(false);
  const [uploadStatusMessage, setUploadStatusMessage] = useState<string>('');
  const [batchProgress, setBatchProgress] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hiddenInputRef = React.useRef<HTMLInputElement>(null);
  const zipInputRef = React.useRef<HTMLInputElement>(null);
  const currentRawFilesRef = React.useRef<BulkFileUploadItem[]>([]);

  const features = useSelector(({ features }: State) => features);
  const isCaseImporterV2 = isRequiredFeatureEnabled(
    features,
    FeatureFlag.CASE_IMPORTER_V2,
  );

  React.useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.webkitdirectory = true;
    }
  }, []);

  const logsColumns = [
    { label: 'Folder Name', key: 'folderName' },
    { label: 'File Name', key: 'filename' },
    { label: 'File Validation Status', key: 'status' },
    { label: 'Upload Status', key: 'uploadStatus' },
    { label: 'Error', key: 'error' },
  ];

  const logsTableData = uploadEvidences
    .toSorted((a, b) => a.folderName.localeCompare(b.folderName))
    .map((cell, index) => ({
      cellData: [
        cell.folderName,
        cell.filename,
        cell.validated ? 'Validated' : 'Failed',
        cell.uploadStatus.toLowerCase(),
        cell.error,
      ],
      row: `index-${index}`,
      data: {},
    }));

  const updateEvidenceLog = (
    file: { filename: string; filepath: string },
    update: { [key: string]: string },
  ) => {
    setUploadEvidences((prevUploadEvidences) =>
      prevUploadEvidences.map((e) =>
        e.filename === file.filename &&
        (file.filepath === `${e.folderName}/${file.filename}` ||
          file.filepath.includes(`/${e.folderName}/`))
          ? { ...e, ...update }
          : e,
      ),
    );
  };

  const uploadFile = async (file: FileUploadResult) => {
    if (file.validated === false) {
      return;
    }
    const fileData = currentRawFilesRef.current.find(
      (f) => f.filename === file.filename && f.filepath === file.filepath,
    );
    if (!fileData || !fileData.file) {
      updateEvidenceLog(file, {
        uploadStatus: 'Failed',
        error: 'File not found in selected files',
      });
      return;
    }

    if (!file.presignedUrl) {
      updateEvidenceLog(file, {
        uploadStatus: 'Failed',
        error: 'Presigned URL is missing',
      });
      return;
    }

    try {
      await uploadToPresignedUrl({
        file: fileData.file,
        url: file.presignedUrl,
        tags: { uploadSource: UploadSource.ReshubBulkImport },
      });
      updateEvidenceLog(file, { uploadStatus: 'Success' });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Upload failed due to unknown error';
      updateEvidenceLog(file, { uploadStatus: 'Failed', error: message });
    }
  };

  const { mutateAsync: confirmBatch } =
    useValidateBulkCaseUploadAndGenerateUrlMutation();

  const triggerFolderSelect = () => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.click();
    }
  };

  const triggerZipSelect = () => {
    if (zipInputRef.current) {
      zipInputRef.current.click();
    }
  };

  const mapFileObject = (files: FileList, includeFile: boolean) =>
    Array.from(files)
      .filter((file) => !isFilesystemMetadataEntry(file.webkitRelativePath))
      .filter((file) => file.webkitRelativePath.includes('/'))
      .map((file) => ({
        filename: file.name ?? 'unknown',
        filepath: file.webkitRelativePath,
        size: file.size,
        mimeType: file.type,
        fileExtension: file.name.split('.').pop() || '',
        ...(includeFile ? { file } : {}),
      }))
      .filter((file) => file.filepath && file.size);

  const handleFolderUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files) return;

    const formattedFilesWithFile = mapFileObject(files, true);
    const formattedFiles = mapFileObject(files, false);
    currentRawFilesRef.current = formattedFilesWithFile;

    if (formattedFiles.length === 0) {
      handleErrorToast({
        toastMessage: 'No valid files found',
        toastDescription:
          'The selected folder contains no uploadable files. Please check the folder contents and try again.',
      });
      return;
    }

    const batches = batchFiles(formattedFiles, BATCH_SIZE);
    setIsLoading(true);
    onUploadingChange?.(true);

    try {
      for (let i = 0; i < batches.length; i++) {
        setBatchProgress(`Uploading batch ${i + 1} of ${batches.length}`);

        let batchData;
        try {
          batchData = await confirmBatch({
            files: batches[i],
            uploadSource: UploadSource.ReshubBulkImport,
            importedCaseIds,
          });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'Batch upload request failed';
          const failedItems: EvidenceItem[] = batches[i].map((file) => {
            const folderName =
              file.filepath.split('/').filter(Boolean).slice(-2, -1)[0] ??
              file.filename;
            return {
              folderName,
              filename: file.filename,
              validated: false,
              uploadStatus: 'Failed',
              presignedUrl: '',
              s3path: '',
              mimeType: file.mimeType,
              size: file.size,
              fileExtension: file.fileExtension,
              error: message,
            };
          });
          setUploadEvidences((prev) => [...prev, ...failedItems]);
          handleErrorToast({
            toastMessage: `Batch ${i + 1} of ${batches.length} failed`,
            toastDescription: message,
          });
          continue;
        }

        const batchEvidenceItems: EvidenceItem[] =
          batchData.validateBulkCaseUploadAndGenerateUrl.processedFolders.flatMap(
            (folder) =>
              folder.files.map((file) => ({
                folderName: folder.folderName,
                filename: file.filename,
                validated: file.validated,
                uploadStatus: file.uploadStatus,
                presignedUrl: file.presignedUrl ?? '',
                error: file.error ?? '',
                s3path: file.s3path ?? '',
                mimeType: file.mimeType,
                size: file.size,
                fileExtension: file.fileExtension,
              })),
          );
        setUploadEvidences((prev) => [...prev, ...batchEvidenceItems]);

        if (i === batches.length - 1) {
          setUploadStatusMessage(
            batchData.validateBulkCaseUploadAndGenerateUrl.message || '',
          );
        }

        const filesToUpload =
          batchData.validateBulkCaseUploadAndGenerateUrl.processedFolders.flatMap(
            (folder) => folder.files,
          );
        await uploadWithConcurrency(
          filesToUpload.map((file) => () => uploadFile(file)),
          UPLOAD_CONCURRENCY,
        );
      }
    } finally {
      setBatchProgress('');
      onUploadingChange?.(false);
      setIsLoading(false);
    }
  };

  const buildItemsFromZip = async (
    zipFile: File,
  ): Promise<BulkFileUploadItem[]> => {
    const zip = await JSZip.loadAsync(zipFile);
    const entries: JSZip.JSZipObject[] = [];
    zip.forEach((_relativePath, entry) => entries.push(entry));

    const fileEntries = entries.filter(
      (entry) => !entry.dir && !isFilesystemMetadataEntry(entry.name),
    );

    const totalDeclaredSize = fileEntries.reduce(
      (total, entry) => total + (getDeclaredUncompressedSize(entry) ?? 0),
      0,
    );
    if (totalDeclaredSize > MAX_ZIP_UNCOMPRESSED_TOTAL_SIZE_IN_BYTES) {
      throw new Error(
        `Zip archive contents are too large (${Math.round(
          totalDeclaredSize / (1024 * 1024),
        )}MB uncompressed). Maximum supported size is ${
          MAX_ZIP_UNCOMPRESSED_TOTAL_SIZE_IN_BYTES / (1024 * 1024)
        }MB.`,
      );
    }

    const items = await Promise.all(
      fileEntries.map(async (entry) => {
        const filename = entry.name.split('/').pop() || entry.name;
        const fileExtension = filename.split('.').pop() || '';
        const mimeType = zipMimeTypeForExtension(fileExtension);
        const declaredSize = getDeclaredUncompressedSize(entry);

        if (
          declaredSize !== undefined &&
          declaredSize > MAX_EXTRACTED_FILE_SIZE_IN_BYTES
        ) {
          return {
            filename,
            filepath: entry.name,
            size: declaredSize,
            mimeType,
            fileExtension,
          };
        }

        const blob = await entry.async('blob');
        const file = new File([blob], filename, { type: mimeType });
        return {
          filename,
          filepath: entry.name,
          size: file.size,
          mimeType,
          fileExtension,
          file,
        };
      }),
    );

    return items.filter((item) => item.filepath);
  };

  const handleZipUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const zipFile = event.target.files?.[0];
    event.target.value = '';
    if (!zipFile) return;

    if (zipFile.size > MAX_ZIP_ARCHIVE_SIZE_IN_BYTES) {
      handleErrorToast({
        toastMessage: 'Oops!',
        toastDescription: `Zip archive is too large (${Math.round(
          zipFile.size / (1024 * 1024),
        )}MB). Maximum supported size is ${
          MAX_ZIP_ARCHIVE_SIZE_IN_BYTES / (1024 * 1024)
        }MB.`,
      });
      return;
    }

    setIsParsingZip(true);
    try {
      const items = await buildItemsFromZip(zipFile);
      if (items.length === 0) {
        throw new Error('The selected zip archive does not contain any files.');
      }
      currentRawFilesRef.current = items;
      const data = await confirmBatch({
        files: items.map(({ file, ...rest }) => rest),
        uploadSource: UploadSource.ReshubBulkImport,
        importedCaseIds,
      });
      const evidenceItems: EvidenceItem[] =
        data.validateBulkCaseUploadAndGenerateUrl.processedFolders.flatMap(
          (folder) =>
            folder.files.map((file) => ({
              folderName: folder.folderName,
              filename: file.filename,
              validated: file.validated,
              uploadStatus: file.uploadStatus,
              presignedUrl: file.presignedUrl ?? '',
              error: file.error ?? '',
              s3path: file.s3path ?? '',
              mimeType: file.mimeType,
              size: file.size,
              fileExtension: file.fileExtension,
            })),
        );
      setUploadEvidences((prev) => [...prev, ...evidenceItems]);
      setUploadStatusMessage(
        data.validateBulkCaseUploadAndGenerateUrl.message || '',
      );
      const filesToUpload =
        data.validateBulkCaseUploadAndGenerateUrl.processedFolders.flatMap(
          (folder) => folder.files,
        );
      onUploadingChange?.(true);
      try {
        await uploadWithConcurrency(
          filesToUpload.map((file) => () => uploadFile(file)),
          UPLOAD_CONCURRENCY,
        );
      } finally {
        onUploadingChange?.(false);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'We encountered a problem while reading your zip archive. Please try again.';
      handleErrorToast({ toastMessage: 'Oops!', toastDescription: message });
    } finally {
      setIsParsingZip(false);
    }
  };

  if (!isCaseImporterV2) {
    return null;
  }

  return (
    <UploadEvidenceContainer data-testid="upload-evidence-container">
      <ButtonRow>
        <SpinnerButton
          data-testid="upload-folder-button"
          loading={isLoading || isParsingZip}
          variant="tertiary"
          onClick={triggerFolderSelect}
        >
          📁 Upload evidences
        </SpinnerButton>

        <SpinnerButton
          data-testid="upload-zip-button"
          loading={isLoading || isParsingZip}
          variant="tertiary"
          onClick={triggerZipSelect}
        >
          📦 Upload ZIP
        </SpinnerButton>
      </ButtonRow>

      <input
        ref={hiddenInputRef}
        type="file"
        multiple
        onChange={handleFolderUpload}
        style={{ display: 'none' }}
        data-testid="folder-upload-input"
      />

      <input
        ref={zipInputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        onChange={handleZipUpload}
        style={{ display: 'none' }}
        data-testid="zip-upload-input"
      />

      {batchProgress && <UploadMessage>{batchProgress}</UploadMessage>}

      <UploadMessage onClick={() => setIsModalOpen(true)}>
        {uploadStatusMessage}
      </UploadMessage>

      <Modal
        size="large"
        title="Upload Status"
        open={isModalOpen}
        setOpen={setIsModalOpen}
        onCancel={() => setIsModalOpen(false)}
        hideButtons={true}
      >
        <PaginatedTable
          columns={logsColumns}
          tableData={logsTableData}
          showPagination={false}
          visibleColumns={logsColumns}
          sortableColumns={[]}
          data-testid="upload-evidence-log-table"
          fixedRowHeight={true}
          isFullWidth={true}
          emptyText={'No upload audit log entries.'}
        />
      </Modal>
    </UploadEvidenceContainer>
  );
};
